import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { concernPath } from "@/data/concerns";
import { ArrowRight } from "lucide-react";
import { photoSrc } from "@/data/photos";
import { getTeamMember } from "@/data/content";
import { ConciergeButton } from "@/components/Concierge";

export const scrollToId = (id, offset = -88) => {
  const target = document.getElementById(id);
  if (!target) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(target, { offset, duration: 1.2 });
  } else {
    target.scrollIntoView({ behavior: "smooth" });
  }
};


export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <p className={`eyebrow ${className}`} data-testid="eyebrow-label">{children}</p>
);

export const GoldRule = ({ className = "" }) => (
  <div className={`gold-rule ${className}`} aria-hidden="true" />
);

export const Photo = ({ photo, className = "", imgClassName = "", frame = false, eager = false, width = 1600, testId }) => (
  <div className={`photo ${className}`} data-testid={testId}>
    <img
      src={photoSrc(photo, width)}
      alt={photo.alt}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      style={photo.pos ? { objectPosition: photo.pos } : undefined}
      className={`${photo.raw ? "" : "filmic"} absolute inset-0 w-full h-full object-cover ${imgClassName}`}
    />
    {frame && <div className="gold-frame inset-3 z-10" aria-hidden="true" />}
  </div>
);

export const PageBand = ({ eyebrow, title, standfirst, image, cta = true, children }) => (
  <section className="relative h-[70vh] min-h-[480px] max-h-[640px] overflow-hidden flex items-end" data-testid="page-hero">
    <div className="absolute inset-0" aria-hidden="true">
      <img
        src={photoSrc(image, 2000)}
        alt=""
        loading="eager"
        decoding="async"
        style={image.pos ? { objectPosition: image.pos } : undefined}
        className={`${image.raw ? "" : "filmic"} absolute inset-0 w-full h-full object-cover ${image.className || ""}`}
      />
      <div className="absolute inset-0 bg-[rgba(35,58,53,0.28)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[rgba(35,58,53,0.55)] via-transparent to-[rgba(35,58,53,0.15)]" />
    </div>
    <div className="gold-frame inset-6 z-10" aria-hidden="true" />
    <div className="relative z-10 shell pb-20 w-full">
      {eyebrow && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="eyebrow eyebrow-dark"
        >
          {eyebrow}
        </motion.p>
      )}
      <h1 className="text-warm-white max-w-3xl">
        <span className="mask-line">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {title}
          </motion.span>
        </span>
      </h1>
      {standfirst && (
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="standfirst text-cream/90 max-w-xl"
          data-testid="page-hero-standfirst"
        >
          {standfirst}
        </motion.p>
      )}
      {cta && (
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8"
        >
          <ConciergeButton testId="page-hero-book-button" className="btn-gold inline-block px-7 py-3.5 text-sm font-medium tracking-[0.06em]" />
        </motion.div>
      )}
      {children}
    </div>
  </section>
);

export const CTABand = ({ title = "Start with a consultation", text = "New patients are assessed before anything is booked. Honest advice, natural results." }) => (
  <section className="bg-deep-sea relative overflow-hidden" data-testid="cta-band">
    <div className="gold-frame inset-5 sm:inset-7 opacity-70" aria-hidden="true" />
    <div className="max-w-3xl mx-auto px-8 py-24 sm:py-28 text-center relative">
      <Reveal>
        <h2 className="text-warm-white">{title}</h2>
        <p className="standfirst text-mist max-w-xl mx-auto">{text}</p>
        <div className="mt-10">
          <ConciergeButton testId="cta-band-book-button" className="btn-outline-gold inline-block px-8 py-4 text-sm font-medium tracking-[0.06em]" />
        </div>
      </Reveal>
    </div>
  </section>
);

export const RiskPanel = ({ risks }) => (
  <div className="bg-cream relative p-8 sm:p-10" data-testid="risk-panel">
    <div className="gold-frame inset-3 opacity-80" aria-hidden="true" />
    <div className="relative">
      <h3 className="text-deep-sea">Risks and what to expect</h3>
      <ul className="mt-5 space-y-3">
        {risks.map((risk, i) => (
          <li key={i} className="flex gap-3 text-ink/85 text-[15px] leading-relaxed">
            <span className="w-1.5 h-1.5 rounded-full bg-gold mt-2.5 shrink-0" aria-hidden="true" />
            {risk}
          </li>
        ))}
      </ul>
      <p className="text-soft-grey text-sm mt-6 leading-relaxed">
        Any surgical or invasive procedure carries risks. Before proceeding, you should seek a second opinion from an
        appropriately qualified health practitioner.
      </p>
    </div>
  </div>
);

export const PortraitArt = ({ photo, name, className = "" }) => {
  if (photo) {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <img
          src={photo}
          alt={name || ""}
          loading="lazy"
          decoding="async"
          className="filmic absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "50% 18%" }}
        />
      </div>
    );
  }
  return (
    <div className={`relative bg-cream flex flex-col items-center justify-center text-center px-6 ${className}`} data-testid="portrait-placeholder">
      <div className="gold-frame inset-3 opacity-70" aria-hidden="true" />
      <span className="font-serif font-normal text-deep-sea text-2xl leading-snug">{name}</span>
    </div>
  );
};

export const FocusChips = ({ items, size = "sm" }) => (
  <div className="flex flex-wrap gap-2" data-testid="focus-chips">
    {items.map((item) => (
      <Link
        key={item.label}
        to={item.to}
        data-testid={`focus-chip-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`${size === "sm" ? "px-3 py-1.5 text-[10px]" : "px-5 py-2.5 text-xs"} bg-white border border-hairline/60 text-deep-sea tracking-[0.12em] uppercase rounded-full hover:border-gold hover:text-gold transition-colors duration-300`}
      >
        {item.label}
      </Link>
    ))}
  </div>
);

export const PractitionerCard = ({ member, index = 0, chips = false, excerpt = false }) => (
  <Reveal delay={index * 0.08}>
    <div className="card-float overflow-hidden group h-full flex flex-col" data-testid={`practitioner-card-${member.id}`}>
      <Link to={`/team/${member.slug}`} data-testid={`practitioner-link-${member.id}`} className="block">
        <PortraitArt photo={member.photo} name={member.name} className="aspect-[4/5] transition-transform duration-700 group-hover:scale-[1.02]" />
        <div className="px-7 pt-7">
          <Eyebrow>{member.title}</Eyebrow>
          <h3 className="text-deep-sea mt-2">{member.name}</h3>
          <p className="font-serif italic text-sea-green mt-1.5">{member.descriptor}</p>
        </div>
      </Link>
      <div className="px-7 pb-7 mt-auto">
        {excerpt && member.bio?.[0] && (
          <p className="text-ink/75 text-sm mt-4 leading-relaxed" data-testid={`practitioner-excerpt-${member.id}`}>
            {member.bio[0].split(". ")[0]}.
          </p>
        )}
        {chips && member.focus?.length > 0 && (
          <div className="mt-5">
            <p className="eyebrow mb-3">Areas of focus</p>
            <FocusChips items={member.focus} />
          </div>
        )}
        <Link
          to={member.cardLink?.to || `/team/${member.slug}`}
          data-testid={`practitioner-cta-${member.id}`}
          className="inline-flex items-center gap-2 mt-5 text-sea-green hover:text-gold text-[11px] tracking-[0.18em] uppercase font-medium transition-colors duration-300"
        >
          {member.cardLink?.label || "View profile"}
          <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  </Reveal>
);

export const ConcernTile = ({ concern, index = 0 }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <Link
      to={concernPath(concern.family, concern.slug)}
      data-testid={`concern-tile-${concern.slug}`}
      className="block bg-cream rounded-lg p-7 h-full group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_4px_24px_rgba(58,58,58,0.10)]"
    >
      <h3 className="text-deep-sea">{concern.title}</h3>
      <p className="text-ink/70 text-sm mt-2 leading-relaxed">{concern.standfirst}</p>
      <span className="inline-flex items-center gap-2 mt-4 text-gold text-[11px] tracking-[0.18em] uppercase font-medium">
        <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
      </span>
    </Link>
  </Reveal>
);

export const Chips = ({ items, basePath }) => (
  <div className="flex flex-wrap gap-3">
    {items.map((item) => (
      <Link
        key={item.to}
        to={item.to}
        data-testid={`chip-${item.to.split("/").pop()}`}
        className="px-5 py-2.5 bg-white border border-hairline/60 text-deep-sea text-xs tracking-[0.12em] uppercase rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
      >
        {item.label}
      </Link>
    ))}
  </div>
);

export const CONCERN_CHIP = (path, label) => ({ to: concernPath(...path.split("/")), label });

export const inputClass =
  "w-full bg-white border border-ink/15 px-4 py-3.5 text-sm text-ink placeholder:text-soft-grey/70 focus:border-gold focus:outline-none transition-colors duration-300 rounded-lg";

export const Honeypot = ({ register }) => (
  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none" }}>
    <label>
      Website
      <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
    </label>
  </div>
);

export const ConsentNote = () => (
  <p className="text-soft-grey text-xs leading-relaxed" data-testid="privacy-collection-notice">
    We collect this information to respond to your enquiry. See our{" "}
    <Link to="/privacy" className="link-sea">Privacy Policy</Link> for how we handle it.
  </p>
);

export const ComplianceNote = () => (
  <p className="text-soft-grey text-xs leading-relaxed" data-testid="compliance-note">
    All new patients require a consultation and suitability assessment. Treatments are prescribed only where
    clinically appropriate. Individual results vary. Services are provided to adults 18 years and over. Any surgical
    or invasive procedure carries risks — before proceeding, you should seek a second opinion from an appropriately
    qualified health practitioner.
  </p>
);
